import { Checkbox, Col, Form, Input, Radio, Row } from "antd";
import { FileType } from "@ifa/utils";

import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import { SelectInput } from "../../components/form/SelectInput";
import { HighPremiumOrderCard } from "./components/HighPremiumOrderCard";
import type { RiskAssessmentQuestionSectionProps } from "./RiskAssessmentForm.shared";

const thirdPartyRelationOptions = [
  { label: "配偶", value: "SPOUSE" },
  { label: "父母", value: "PARENT" },
  { label: "子女", value: "CHILD" },
  { label: "受益人", value: "BENEFICIARY" },
  { label: "其他", value: "OTHER" },
];

export function RiskAssessmentGenericSections(props: RiskAssessmentQuestionSectionProps) {
  return (
    <>
      <ClientBackgroundSection isReadOnly={props.isReadOnly} />
      <IdentityAddressSection isReadOnly={props.isReadOnly} />
      <FinancialStatusSection isReadOnly={props.isReadOnly} />
      <PremiumPaymentSection {...props} />
      <DistributionChannelSection form={props.form} isReadOnly={props.isReadOnly} />
      <DistributionFaceSection form={props.form} isReadOnly={props.isReadOnly} />
      <CountryRiskSection form={props.form} isReadOnly={props.isReadOnly} />
      <ProductRiskSection {...props} />
    </>
  );
}

function ClientBackgroundSection({ isReadOnly }: Pick<RiskAssessmentQuestionSectionProps, "isReadOnly">) {
  return (
    <CustomCard title="客戶風險（客戶背景）" className="mb-4" bordered={false}>
      <Form.Item name="pep" label="客戶是否是政治公眾人物（PEP）嗎?" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="fullUboAndDirectorList" label="（僅限公司客戶）是否擁有所有實益擁有人和董事/控制人的完整名單" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly || true}>
          <Radio value="A">A: 是(公司客戶)</Radio>
          <Radio value="B">B: 否(公司客戶)</Radio>
          <Radio value="C">C: N/A(個人客戶)</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item label="您的客戶有如下情況嗎？" required>
        <div style={{ background: "#F5F5F5", padding: "16px", borderRadius: "4px", marginBottom: "16px" }}>
          <Row gutter={[16, 8]}>
            <Col span={12}>高淨值人士（淨值或投資資產&gt;1.5 億港元）</Col>
            <Col span={12}>從事石油和天然氣行業</Col>
            <Col span={12}>非營利組織</Col>
            <Col span={12}>支付處理商</Col>
          </Row>
        </div>
        <Form.Item name="customerMediumRiskSituation" noStyle rules={[{ required: true, message: "請選擇" }]}>
          <Radio.Group disabled={isReadOnly}>
            <Radio value="A" style={{ marginRight: 40 }}>A. 是</Radio>
            <Radio value="B">B. 否</Radio>
          </Radio.Group>
        </Form.Item>
      </Form.Item>

      <Form.Item label="您的客戶有如下情況嗎？" required>
        <div style={{ background: "#F5F5F5", padding: "16px", borderRadius: "4px", marginBottom: "16px" }}>
          <Row gutter={[16, 8]}>
            <Col span={12}>從事賭場和賭博行業</Col>
            <Col span={12}>貴金屬 / 寶石 / 寶石經銷商</Col>
            <Col span={12}>軍火商 / 製造商</Col>
            <Col span={12}>貨幣服務業務</Col>
            <Col span={12}>超高淨值客戶（淨值或投資資產 &gt; 2.5 億港元）</Col>
            <Col span={12}>客戶有能力發行不記名股票的客戶（如果已知）</Col>
          </Row>
        </div>
        <Form.Item name="customerHighRiskSituation" noStyle rules={[{ required: true, message: "請選擇" }]}>
          <Radio.Group disabled={isReadOnly}>
            <Radio value="A" style={{ marginRight: 40 }}>A. 是</Radio>
            <Radio value="B">B. 否</Radio>
          </Radio.Group>
        </Form.Item>
      </Form.Item>
    </CustomCard>
  );
}

function IdentityAddressSection({ isReadOnly }: Pick<RiskAssessmentQuestionSectionProps, "isReadOnly">) {
  return (
    <CustomCard title="客戶風險（身份和地址驗證）" className="mb-4" bordered={false}>
      <Form.Item name="validIdentityDocuments" label="您的客戶是否提供了可接受的標準身份驗證檔案，例如身份證（對於個人客戶）或公司註冊證書（對於公司客戶）？" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="cooperative" label="您的客戶在過程中是否配合？ （不拖延提供身分和地址證明文件，回答所有問題等）" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>
    </CustomCard>
  );
}

function FinancialStatusSection({ isReadOnly }: Pick<RiskAssessmentQuestionSectionProps, "isReadOnly">) {
  return (
    <CustomCard title="客戶風險（客戶的財務狀況）" className="mb-4" bordered={false}>
      <Form.Item name="sourceOfFundsConsistent" label="客戶陳述的財富來源 / 資金來源以及涉及的金額是否與您對客戶的了解相符，例如考慮到他們 的年齡和職業？" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="notHighRiskBusiness" label="您的客戶不涉及參與 / 經營高風險或高現金週轉業務？" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>
    </CustomCard>
  );
}

function PremiumPaymentSection({ form, isReadOnly, renderUploadBox }: Pick<RiskAssessmentQuestionSectionProps, "form" | "isReadOnly" | "renderUploadBox">) {
  return (
    <CustomCard title="保費支付方式待確認如下" className="mb-4" bordered={false}>
      <Form.Item name="paidInitialPremium" label="投保申請時是否繳付了首期保費" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="fromOwnAccount" label="保費/投資金額是否來自客戶自己的賬戶" rules={[{ required: true }]}>
        <Radio.Group
          disabled={isReadOnly}
          onChange={(e) => {
            if (e.target.value === "A") {
              form.setFieldsValue({
                thirdPartyPayerRelation: undefined,
                thirdPartyPayerInfo: undefined,
              });
            }
          }}
        >
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, current) => prev.fromOwnAccount !== current.fromOwnAccount}>
        {({ getFieldValue }) => {
          const isThirdPartyPayer = getFieldValue("fromOwnAccount") === "B";
          return isThirdPartyPayer ? (
            <>
              <Form.Item name="thirdPartyPayerRelation" label="與第三方付款人關係" rules={[{ required: true, message: "請選擇關係" }]}>
                <SelectInput title="與第三方付款人關係" placeholder="請選擇關係" readOnly={isReadOnly} options={thirdPartyRelationOptions} />
              </Form.Item>

              <Form.Item label="上載關係證明檔案" required>
                <div className="custom-upload-wrapper">{renderUploadBox(FileType.CraThiredPartyRelaTionShipCertificate, "第三方付款人關係證明")}</div>
              </Form.Item>

              <Form.Item name="thirdPartyPayerInfo" label="備註（選填）">
                <Input.TextArea size="large" placeholder="請輸入其他補充資料" rows={3} disabled={isReadOnly} />
              </Form.Item>
            </>
          ) : null;
        }}
      </Form.Item>

      <Form.Item name="initialPremiumPaymentMethod" label="客戶計劃使用哪種或哪些方法來支付初始保費？（選一項或多項）" rules={[{ required: true }]}>
        <Checkbox.Group style={{ width: "100%" }} disabled={isReadOnly}>
          <Row>
            <Col span={12}><Checkbox value="A">A: 信用卡</Checkbox></Col>
            <Col span={12}><Checkbox value="B">B: 銀行本票</Checkbox></Col>
            <Col span={12}><Checkbox value="C">C: 電匯</Checkbox></Col>
            <Col span={12}><Checkbox value="D">D: 銀行轉賬</Checkbox></Col>
            <Col span={12}><Checkbox value="E">E: 現金(透過保險公司或銀行櫃檯支付)</Checkbox></Col>
            <Col span={12}><Checkbox value="F">F: 自動扣款</Checkbox></Col>
            <Col span={12}><Checkbox value="G">G: 支票</Checkbox></Col>
          </Row>
        </Checkbox.Group>
      </Form.Item>
    </CustomCard>
  );
}

function DistributionChannelSection({ form, isReadOnly }: Pick<RiskAssessmentQuestionSectionProps, "form" | "isReadOnly">) {
  return (
    <CustomCard title="分銷/交付風險（分銷管道）" className="mb-4" bordered={false}>
      <Form.Item name="referredByThirdParty" label="客戶是否由第三方介紹" rules={[{ required: true }]}>
        <Radio.Group
          disabled={isReadOnly}
          onChange={(e) => {
            if (e.target.value === "B" || e.target.value === "C") {
              form.setFieldsValue({
                instructionThroughThirdParty: "C",
                thirdPartyInstructionProof: undefined,
              });
            } else if (e.target.value === "A") {
              form.setFieldsValue({
                instructionThroughThirdParty: "B",
                thirdPartyInstructionProof: undefined,
              });
            }
          }}
        >
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
          <Radio value="C">C: N/A(介紹人是公司轉介人)</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, current) => prev.referredByThirdParty !== current.referredByThirdParty}>
        {({ getFieldValue }) => {
          const referredByThirdParty = getFieldValue("referredByThirdParty");
          const isDisabled = referredByThirdParty === "A" || referredByThirdParty === "B" || referredByThirdParty === "C";

          return (
            <Form.Item label="客戶的投保指示是否通過第三方傳達？" required={referredByThirdParty === "A"}>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <Form.Item name="instructionThroughThirdParty" noStyle rules={[{ required: referredByThirdParty === "A", message: "請選擇" }]}>
                  <Radio.Group disabled={isReadOnly || isDisabled}>
                    <Radio value="A">A. 是</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, current) => prev.instructionThroughThirdParty !== current.instructionThroughThirdParty}>
                  {({ getFieldValue: getInstructionValue }) => {
                    const isYes = getInstructionValue("instructionThroughThirdParty") === "A";
                    return (
                      <Form.Item name="thirdPartyInstructionProof" noStyle rules={[{ required: isYes, message: "請輸入原因" }]}>
                        <Input size="large" placeholder="請輸入原因" disabled={isReadOnly || !isYes} style={{ width: 200, marginRight: 24 }} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>

                <Form.Item name="instructionThroughThirdParty" noStyle>
                  <Radio.Group disabled={isReadOnly || isDisabled}>
                    <Radio value="B" style={{ marginRight: 24 }}>B. 否</Radio>
                    <Radio value="C">C. N/A</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>
            </Form.Item>
          );
        }}
      </Form.Item>
    </CustomCard>
  );
}

function DistributionFaceSection({ form, isReadOnly }: Pick<RiskAssessmentQuestionSectionProps, "form" | "isReadOnly">) {
  return (
    <CustomCard title="分銷/交付風險（面對面溝通）" className="mb-4" bordered={false}>
      <Form.Item name="faceToFaceCommunication" label="在投保過程中，您是否經面對面方式與客戶進行溝通? " rules={[{ required: true }]}>
        <Radio.Group
          disabled={isReadOnly}
          onChange={(e) => {
            if (e.target.value === "A") {
              form.setFieldsValue({ needsNonFaceToFaceSafeguards: "C" });
            } else if (e.target.value === "B") {
              form.setFieldsValue({ needsNonFaceToFaceSafeguards: undefined });
            }
          }}
        >
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, current) => prev.faceToFaceCommunication !== current.faceToFaceCommunication}>
        {({ getFieldValue }) => {
          const faceToFaceCommunication = getFieldValue("faceToFaceCommunication");
          const isDisabled = faceToFaceCommunication === "A";

          return (
            <Form.Item
              name="needsNonFaceToFaceSafeguards"
              label="在與客戶(非面對面）溝通的情況下，你是否認為需要有適當的保障措施來確認客戶行為或協議的真實性和 合法性 (包括：公證人見證簽名, 律師事務所保證協議合法, 會計師驗證財務文件等)"
              rules={[{ required: faceToFaceCommunication === "B", message: "請選擇" }]}
            >
              <Radio.Group disabled={isReadOnly || isDisabled}>
                <Radio value="A">A: 是</Radio>
                <Radio value="B" disabled>B: 否</Radio>
                <Radio value="C" disabled>C: N/A</Radio>
              </Radio.Group>
            </Form.Item>
          );
        }}
      </Form.Item>
    </CustomCard>
  );
}

function CountryRiskSection({ form, isReadOnly }: Pick<RiskAssessmentQuestionSectionProps, "form" | "isReadOnly">) {
  return (
    <CustomCard title="國家或地區風險" className="mb-4" bordered={false}>
      <Form.Item name="residentInHK" label="您的客戶定居在香港嗎？" rules={[{ required: true }]}>
        <Radio.Group
          disabled={isReadOnly}
          onChange={(e) => {
            if (e.target.value === "A" || !e.target.value) {
              form.setFieldsValue({ nonHKResidenceCategory: undefined });
            }
          }}
        >
          <Radio value="A">A: 是(LOW RISK)</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, current) => prev.residentInHK !== current.residentInHK}>
        {({ getFieldValue }) => {
          const residentInHK = getFieldValue("residentInHK");
          if (!residentInHK || residentInHK === "A") {
            return null;
          }

          return (
            <Form.Item name="nonHKResidenceCategory" label="如果您的客戶不在香港定居，他/她是否位於下列國家之一？" rules={[{ required: residentInHK === "B", message: "請選擇" }]}> 
              <Radio.Group style={{ display: "flex", flexDirection: "column", gap: 12 }} disabled={isReadOnly}>
                <Radio value="A" style={{ alignItems: "flex-start" }}>
                  <span style={{ whiteSpace: "normal" }}>A. 澳大利亞，奧地利，比利時，加拿大，捷克共和國，丹麥，芬蘭，法國，德國，盧森堡，荷蘭，葡萄牙，斯洛文尼亞，西班牙，瑞典，美國，英國</span>
                </Radio>
                <Radio value="B" style={{ alignItems: "flex-start" }}>
                  <span style={{ whiteSpace: "normal" }}>B. 文萊，中國，愛沙尼亞，希臘，日本，韓國，立陶宛，波蘭，卡塔爾，新加坡，中國臺灣，泰國</span>
                </Radio>
                <Radio value="C" style={{ alignItems: "flex-start" }}>
                  <span style={{ whiteSpace: "normal" }}>C. 阿根廷，巴哈馬，巴林，孟加拉，巴巴多斯，百慕大，玻利維亞，巴西，保加利亞，柬埔寨，喀麥隆，賽普勒斯，多明尼加，杜拜，厄瓜多爾，埃及，埃塞俄比亞，斐濟，直布羅陀，關島，根西島，科威特，黎巴嫩，利比里亞，中國澳門，馬達加斯加，馬來西亞，馬爾代夫，馬里，馬爾他，模里西斯，墨西哥，密克羅尼西亞，摩納哥，蒙古，摩洛哥，緬甸，諾魯，尼泊爾，波多黎各，羅馬尼亞，俄羅斯聯邦，沙烏地阿拉伯，塞席爾，索羅門群島，南非，斯里蘭卡，蘇丹，瑞士，敘利亞，委內瑞拉，越南，英屬維京群島 (BVI)，維京群島（美國），也門</span>
                </Radio>
              </Radio.Group>
            </Form.Item>
          );
        }}
      </Form.Item>
    </CustomCard>
  );
}

function ProductRiskSection({ isReadOnly, productData, companyList, annualTotalPremium, totalOneTimePayment, add, edit, handleDelete }: Pick<RiskAssessmentQuestionSectionProps, "isReadOnly" | "productData" | "companyList" | "annualTotalPremium" | "totalOneTimePayment" | "add" | "edit" | "handleDelete">) {
  return (
    <CustomCard title="產品/服務風險" className="mb-4" bordered={false}>
      <Form.Item name="providesAnonymity" label="產品/向客戶提供的產品 / 服務本質上是否提供了更多的匿名性(例如: 信託保單/保險合約的持有人是一個多層架構的企業)？" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="involvesLargeCash" label="提供給客戶的產品/服務是否涉及收取大量現金？" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="frequentPurchasesWithoutReason" label="客戶在過去三個月內多次購買相同類型的保險產品，且沒有有效理由" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="supportsHighFrequencyIrregularActivity" label="這些產品/服務是否有利於高交易頻率和不規則的客戶賬戶活動？" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="allowsLargeWithdrawals" label="產品是否允許大額提款的產品 (例如: 保費融資, 投資相連人壽保險 (一次性繳付保費))" rules={[{ required: true }]}>
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="exceedsHighPremiumThreshold"
        label={
          <>
            在職人士:年繳交保費≥HKD2,000,000 或一次性繳付保費（包含預繳）≥ HKD4,000,000。
            <br />
            非在職人士（家庭主婦/學生/退休人士/無業）或年齡在65歲以上的申請:年繳交保費≥HKD1,000,000 或一次性繳付保費（包含預繳）≥ HKD2,000,000。(累積客戶在過去六個月內購買保險產品的保費)
          </>
        }
        rules={[{ required: true, message: "請選擇是否符合高保費門檻" }]}
      >
        <Radio.Group disabled={isReadOnly}>
          <Radio value="A">A: 是</Radio>
          <Radio value="B">B: 否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, current) => prev.exceedsHighPremiumThreshold !== current.exceedsHighPremiumThreshold}>
        {({ getFieldValue }) =>
          getFieldValue("exceedsHighPremiumThreshold") === "A" ? (
            <div className="mt-4">
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, color: "#333", fontWeight: 500 }}>若上方問題之答案為「是」者，請在下方詳細列舉客戶已投保的保單。</div>
                <div style={{ fontWeight: "bold", fontSize: 16 }}>
                  年繳總交保費： {annualTotalPremium.toLocaleString()}HK$ &nbsp;&nbsp;
                  一次性繳付保費（含預繳）： {totalOneTimePayment.toLocaleString()}HK$
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                {productData.length === 0 ? (
                  <div style={{ padding: 40, textAlign: "center", background: "#fafafa", borderRadius: 8, color: "#999" }}>
                    暫無外部保單，請點擊下方按鈕新增
                  </div>
                ) : (
                  productData.map((order) => (
                    <HighPremiumOrderCard key={order.key} order={order} companyList={companyList} onEdit={() => edit(order)} onDelete={() => handleDelete(order.orderNo)} readOnly={isReadOnly} />
                  ))
                )}
              </div>

              <div className="mt-4">
                <CustomButton bgColor="var(--theme-primary)" color="white" onClick={add} style={{ padding: "0 16px" }} disabled={isReadOnly}>
                  新增外部保單
                </CustomButton>
              </div>
            </div>
          ) : null
        }
      </Form.Item>
    </CustomCard>
  );
}
